import React, { useState, useRef, useEffect, useMemo } from "react";
import { Trash2, SquarePen, Eye, Plus, Search, ArrowLeft, ListRestart } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Pagination from "../utils/pagination/Pagination";
import AddProductForm from "./AddProductForm";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  ModalCloseButton,
} from "@chakra-ui/react";
import SearchProduct from "./SearchProduct";
import { useDispatch, useSelector } from "react-redux";
import { getProduct } from "../../../redux/reduxApi/admin/ProductReduxApi";
import { getEmployeesList } from "../../../redux/reduxApi/admin/EmployeesReduxApi";
import {
  deleteInventory,
  countInventory,
} from "../../../redux/api/admin/InventoryApi";
import { deleteinventoryAction } from "../../../redux/reduxSlice/admin/ProductSlice";
import { Spinner } from "@chakra-ui/react";
import { toast } from "react-toastify";
import { setFilteredStatus } from "../../../redux/reduxSlice/admin/ProductSlice";
import { usePermission } from "../../../../context/PermissionContext";
import { formatDateTime, getFullName } from "../../../../utils/commonFunctions";

function ProductList() {
  const { canAdd, canEdit, canViewAll, canDelete } = usePermission();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const openTrash = () => setIsOpen(true);
  const closeTrash = () => setIsOpen(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordCount, setRecordCount] = useState(0);
  const [showDropdown, setIsShowDropdown] = useState(false);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const getInventory = useSelector((state) => state?.inventory?.inventory);
  const isFiltered = useSelector((state) => state.inventory?.isFiltered);
  const [productList, setProductList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [employees, setEmployees] = useState([]);
  const employeeMap = useMemo(() => {
    return employees.reduce((map, emp) => {
      const key = emp.ROWID || emp.id || emp.employeeId;
      if (key) {
        map[String(key)] = `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.email || "Unknown";
      }
      return map;
    }, {});
  }, [employees]);

  const getEmployeeDisplayName = (employee) => {
    if (!employee) return "-";
    if (typeof employee === "object") return getFullName(employee);
    const lookup = employeeMap[String(employee)];
    return lookup || String(employee);
  };

  // Parse image URLs from itemImages string
  const parseImageUrls = (itemImages) => {
    if (!itemImages) return [];

    try {
      // If it's already an array
      if (Array.isArray(itemImages)) {
        return itemImages
          .map((img) => {
            if (typeof img === "string") return img;
            if (img.filePath) return img.filePath;
            return "";
          })
          .filter((url) => url);
      }

      // If it's a string, try to parse it
      if (typeof itemImages === "string") {
        // Try to parse as JSON
        if (itemImages.startsWith("[") || itemImages.startsWith("{")) {
          const parsed = JSON.parse(itemImages);
          if (Array.isArray(parsed)) {
            return parsed
              .map((img) => {
                if (typeof img === "string") return img;
                if (img.filePath) return img.filePath;
                if (img.url) return img.url;
                return "";
              })
              .filter((url) => url);
          } else if (parsed.filePath) {
            return [parsed.filePath];
          }
        }

        // If it's already a URL
        if (itemImages.startsWith("http")) {
          return [itemImages];
        }

        // Check if it contains filePath
        if (itemImages.includes("filePath=")) {
          const match = itemImages.match(/filePath=([^,}]+)/);
          if (match && match[1]) {
            return [match[1].trim()];
          }
        }
      }

      return [];
    } catch (error) {
      console.error("Error parsing image URLs:", error);
      return [];
    }
  };

  useEffect(() => {
    dispatch(getEmployeesList({ page: 1, size: 100, limit: 100 }))
      .then((response) => {
        const employees = response.payload?.data?.employees || [];
        setEmployees(employees);
      })
      .catch((error) => {
        console.error("Error fetching employees for product list:", error);
      });
  }, [dispatch]);

  // Get the first image URL
  const getFirstImageUrl = (item) => {
    if (!item) return "";

    // Check imageUrls first
    if (Array.isArray(item.imageUrls) && item.imageUrls.length > 0) {
      return item.imageUrls[0];
    }

    // Check itemImages
    const images = parseImageUrls(item.itemImages);
    return images.length > 0 ? images[0] : "";
  };

  // Open and Close Search Drawer
  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleShowDropdown = () => {
    setIsShowDropdown(!showDropdown);
  };

  // Handle checkbox change for individual row
  const handleCheckboxChange = (ROWID) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(ROWID)
        ? prevSelected.filter((itemId) => itemId !== ROWID)
        : [...prevSelected, ROWID],
    );
  };

  // Handle select all rows
  const handleSelectAllRows = () => {
    if (selectedRows.length === productList.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(productList.map((item) => item.ROWID));
    }
  };

  // Get product data
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await dispatch(
          getProduct({ page: currentPage, limit: 30 }),
        );
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching Product data:", error);
      }
    };

    fetchProducts();
  }, [dispatch, currentPage]);

  // Sync with Redux state
  useEffect(() => {
    if (getInventory && getInventory.length > 0) {
      setProductList(getInventory);
    }
  }, [getInventory]);

  // Get total count of products
  const fetchTotalCount = async () => {
    try {
      const response = await countInventory();
      setRecordCount(response.data.count);
    } catch (error) {
      console.error("Error fetching total Product count:", error);
    }
  };

  useEffect(() => {
    fetchTotalCount();
  }, []);

  // Handle delete functionality
  const handleDelete = () => {
    setShowProgressBar(true);
    setLoading(true);

    const idsToDelete =
      selectedRows.length === 0 ? [deleteProductId] : selectedRows;

    Promise.all(idsToDelete.map((id) => deleteInventory(id)))
      .then(() => {
        // Update local state
        setProductList((prevData) =>
          prevData.filter((item) => !idsToDelete.includes(item.ROWID)),
        );

        // Dispatch action to update Redux state
        dispatch(deleteinventoryAction(idsToDelete));

        // Refresh count
        fetchTotalCount();

        toast.success("Product(s) deleted successfully");
      })
      .catch((error) => {
        console.log(error);
        toast.error("Failed to delete product(s)");
      })
      .finally(() => {
        setShowProgressBar(false);
        setLoading(false);
        closeTrash();
        setSelectedRows([]);
        setDeleteProductId("");
      });
  };

  const handleReset = () => {
    setLoading(true);

    dispatch(setFilteredStatus(false));
    sessionStorage.removeItem("productSearchQuery");

    dispatch(getProduct({ page: 1, limit: 30 }))
      .finally(() => setLoading(false));
  };

  const openDrawerWithDetails = (product) => {
    setSelectedProduct(product);
    setIsDrawerOpen(true);
  };

  return (
    <>
      {loading && (
        <div className="spinner-overlay">
          <Spinner size="xl" color="#011638" />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={closeTrash}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Are you sure you want to Delete?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRows.length > 1
              ? `You are about to delete ${selectedRows.length} products. This action cannot be undone.`
              : "This action cannot be undone."}
          </ModalBody>
          <ModalFooter>
            <Button className="main-btn" mr={3} onClick={handleDelete}>
              Delete
            </Button>
            <Button className="cancel-button" onClick={closeTrash}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <div className="flex flex-col min-h-screen relative">
        {/* Header Section */}
        <div className="d-flex justify-content-between mb-1 px-2 items-center">
          {/* Left side: Arrow or More dropdown */}
          <div className="flex items-center">
            {selectedRows.length > 0 ? (
              <button className="main-btn mx-2" onClick={openTrash}>
                <span className="inner-content">
                  <span className="ms-1">
                    {" "}
                    Delete All ({selectedRows.length})
                  </span>
                </span>
              </button>
            ) : (
              <div className="flex items-center">
                <ArrowLeft
                  color="black"
                  size={18}
                  style={{ cursor: "pointer", marginRight: "8px" }}
                  onClick={() => window.history.back()}
                />
                {/* <button className="main-btn" onClick={handleShowDropdown}>
                  <span className="ms-1">More</span>
                </button> */}
                {showDropdown && (
                  <ul
                    className="rounded overflow-hidden ps-0"
                    style={{
                      position: "absolute",
                      zIndex: 9999,
                      left: "1%",
                      top: "100%",
                      maxHeight: "250px",
                      backgroundColor: "#fff",
                      boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <li className="p-1 hover:bg-gray-100">
                      <span
                        style={{ cursor: "pointer" }}
                        className="mx-3"
                        onClick={handleImportClick}
                      >
                        Import
                      </span>
                      <span>
                        {" "}
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                        />
                      </span>
                    </li>
                    <li className="p-1 hover:bg-gray-100">
                      <span style={{ cursor: "pointer" }} className="mx-3">
                        Export
                      </span>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Right side: Search and Add buttons */}
          <div className="flex gap-2 items-center">
            <button
              className="main-btn"
              onClick={isFiltered ? handleReset : openSearch}
            >
              <span className="inner-content">
                {isFiltered ? (
                  <>
                    <ListRestart size="18px" />
                    <span className="ms-1">Show All</span>
                  </>
                ) : (
                  <>
                    <Search size="18px" />
                    <span className="ms-1">Search</span>
                  </>
                )}
              </span>
            </button>
            {/* <Link to="/admin/add-product-form">
              <button
                className="d-flex justify-content-between align-items-center main-btn"
                onClick={openDrawer}
              >
                <span className="inner-content">
                  <Plus size="18px" />
                  <span className="ms-1">Add Item</span>
                </span>
              </button>
            </Link> */}
            {/* <button
  onClick={() => canAdd("item") ? navigate("/admin/add-product-form") : ""}
  className="d-flex justify-content-between align-items-center main-btn"
>
  <span className="inner-content">
    <Plus size="18px" />
    <span className="ms-1">Add Item</span>
  </span>
</button> */}

            {canAdd("item") && (
              <button
                onClick={() => navigate("/admin/inventory/add-product-form")}
                className="d-flex justify-content-between align-items-center main-btn"
              >
                <span className="inner-content">
                  <Plus size="18px" />
                  <span className="ms-1">Add Item</span>
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Table Section */}
        <div className="flex-grow px-1">
          <TableContainer
            className="rounded-lg"
            style={{
              height: "calc(100vh - 160px)",
              backgroundColor: "#fdfdfd",
              border: "1px solid #e7e7e7",
              overflowX: "auto",
              overflowY: "auto",
              whiteSpace: "nowrap",
              scrollbarGutter: "stable both-edges",
              msOverflowStyle: "scrollbar",
              scrollbarWidth: "thin",
            }}
          >
            <Table id="table-to-xls" size="sm">
              <Thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "white",
                }}
              >
                <Tr className="tr-border">
                  <Th className="py-1 th-color">
                    <Checkbox
                      isChecked={
                        selectedRows.length === productList.length &&
                        productList.length > 0
                      }
                      onChange={handleSelectAllRows}
                    />
                  </Th>
                  <Th className="py-1 th-color">Actions </Th>
                  {/* <Th className="py-1 th-color">Image</Th> */}
                  <Th className="py-1 th-color">Mechanism Name</Th>

                  {/* <Th className="py-1 th-color">SKU</Th> */}
                  <Th className="py-1 th-color">Category</Th>
                  <Th className="py-1 th-color">Sub Category</Th>
                  <Th className="py-1 th-color">Price /sq.ft</Th>
                  {/* <Th className="py-1 th-color">Shade No.</Th> */}
                  <Th className="py-1 th-color">Unit</Th>
                  <Th className="py-1 th-color">Created By</Th>
                  <Th className="py-1 th-color">Modified By</Th>
                  <Th className="py-1 th-color">Created Time</Th>
                  <Th className="py-1 th-color">Modified Time</Th>
                  {/* <Th className="py-1 th-color">HSN Code</Th> */}
                </Tr>
              </Thead>
              <Tbody>
                {Array.isArray(productList) && productList.length > 0 ? (
                  productList.map((item) => {
                    const imageUrl = getFirstImageUrl(item);
                    return (
                      <Tr key={item.ROWID}>
                        <Td>
                          <Checkbox
                            isChecked={selectedRows.includes(item.ROWID)}
                            onChange={() => handleCheckboxChange(item.ROWID)}
                          />
                        </Td>
                        <Td>
                          <div className="d-flex gap-1">
                            {/* <Eye
                              color="#011638"
                              size="16px"
                              strokeWidth="1"
                              // onClick={() => navigate("/admin/product-detail")}
                              onClick={() =>
                             canAdd("item")  ?  navigate("/admin/product-detail", {
                                  state: { id: item.ROWID },
                                }): ""
                              }
                              style={{ cursor: "pointer" }}
                              title="View"
                            /> */}

                            {canViewAll("item") && (
                              <Eye
                                color="#011638"
                                size="16px"
                                strokeWidth="1"
                                onClick={() => navigate("/admin/inventory/product-detail", {
                                  state: { id: item.ROWID },
                                })}
                                style={{ cursor: "pointer" }}
                                title="View"
                              />
                            )}
                            {/* <SquarePen
                              color="#011638"
                              size="16px"
                              strokeWidth="1"
                              onClick={() => 
                              canEdit("item") ?   navigate("/admin/update-product-form", {
                                  state: {
                                    isUpdateMode: true,
                                    id: item.ROWID,
                                  },
                                }) : ""
                              }
                              style={{ cursor: "pointer" }}
                              title="Edit"
                            /> */}

                            {canEdit("item") && (
                              <SquarePen
                                color="#011638"
                                size="16px"
                                strokeWidth="1"
                                onClick={() => navigate("/admin/inventory/update-product-form", {
                                  state: {
                                    isUpdateMode: true,
                                    id: item.ROWID,
                                  },
                                })}
                                style={{ cursor: "pointer" }}
                                title="Edit"
                              />
                            )}
                            {/* <Trash2
                              color="#011638"
                              size="16px"
                              strokeWidth="1"
                              onClick={() => { 
                            canDelete ("item") ?   setDeleteProductId(item.ROWID);
                                openTrash();
                              }}
                              style={{ cursor: "pointer" }}
                              title="Delete"
                            /> */}
                            {/* <Trash2
  color="#011638"
  size="16px"
  strokeWidth="1"
  onClick={() => {
    if (canDelete("item")) {
      setDeleteProductId(item.ROWID);
      openTrash();
    }
  }}
  style={{ cursor: "pointer" }}
  title="Delete"
/> */}
                            {canDelete("item") && (
                              <Trash2
                                color="#011638"
                                size="16px"
                                strokeWidth="1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteProductId(item.ROWID);
                                  openTrash();
                                }}
                                style={{ cursor: "pointer" }}
                                title="Delete"
                              />
                            )}             </div>
                        </Td>
                        {/* <Td>
                          <div style={{ height: "40px", width: "40px" }}>
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={item?.itemName || "Product Image"}
                                style={{
                                  height: "40px",
                                  width: "40px",
                                  objectFit: "cover",
                                  borderRadius: "4px"
                                }}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML = `
                                    <div style="
                                      height: 50px;
                                      width: 50px;
                                      background-color: #f0f0f0;
                                      display: flex;
                                      align-items: center;
                                      justify-content: center;
                                      border-radius: 4px;
                                      font-size: 10px;
                                    ">
                                      No Image
                                    </div>
                                  `;
                                }}
                              />
                            ) : (
                              <div style={{
                                height: "40px",
                                width: "40px",
                                backgroundColor: "#f0f0f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "4px",
                                fontSize:"12px"
                              }}>
                                No Image
                              </div>
                            )}
                          </div>
                        </Td> */}
                        <Td
                          className="font-colors cursor-pointer"
                          onClick={() => navigate("/admin/inventory/product-detail")}
                        >
                          {item?.itemName || "N/A"}
                        </Td>
                       
                        {/* <Td className="font-colors">{item?.sku || "N/A"}</Td> */}
                        <Td className="font-colors">
                          {item?.categoryName || "N/A"}
                        </Td>
                        <Td className="font-colors">
                          {item?.subCategory || "N/A"}
                        </Td>
                         <Td className="font-colors">
                          {item?.sellingPrice || "N/A"}
                        </Td>
                        {/* <Td className="font-colors">{item?.shadeNumber || "N/A"}</Td> */}
                        <Td className="font-colors">{item?.unit || "N/A"}</Td>
                        <Td className="font-colors">{getEmployeeDisplayName(item?.createdByUser || item?.createdByDetails || item?.createdBy)}</Td>
                        <Td className="font-colors">{getEmployeeDisplayName(item?.modifiedByUser || item?.modifiedByDetails || item?.modifiedBy)}</Td>
                        <Td className="font-colors">{formatDateTime(item?.CREATEDTIME)}</Td>
                        <Td className="font-colors">{formatDateTime(item?.MODIFIEDTIME)}</Td>
                        {/* <Td className="font-colors">
                          {item?.hscCode || "N/A"}
                        </Td> */}
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan="10" className="text-center py-8">
                      No products found
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>

        {/* Footer with Pagination */}
        <div
          className="w-100 bg-white shadow-sm"
          style={{ position: "sticky", bottom: 0, zIndex: 20 }}
        >
          <div className="row mx-2 flex items-center">
            <div className="col-lg-3 col-md-6 col-12">
              <p className="fw-semibold mb-0">Total Count: {recordCount}</p>
            </div>
            <div className="col-lg-6 col-md-6 col-12 flex justify-center">
              <Pagination
                totalPages={Math.ceil(recordCount / 30)}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
              />
            </div>
            <div className="col-lg-3 col-md-6 col-12"></div>
          </div>
        </div>

        <SearchProduct
          closeDrawer={closeSearch}
          isSearchDrawerOpen={isSearchOpen}
          setRecordCount={setRecordCount}
        />
      </div>
    </>
  );
}

export default ProductList;
